/* eslint-disable no-bitwise -- ZIP container + CRC32 require byte-level bit ops */
// Minimal, dependency-free `.xlsx` writer.
//
// An xlsx file is a ZIP archive of XML parts. We build the required parts and
// pack them with the ZIP "store" (no compression) method so a real spreadsheet
// — with true columns — opens in Excel/Google Sheets/LibreOffice, instead of a
// `;`-separated CSV that collapses into a single column when the separator is
// not recognized.
//
// Usage:
//   downloadXlsxFile('report.xlsx', rows, { sheetName: 'Relatório' });
// where `rows` is an array of rows. A row is an array of cells (or `[]` for a
// blank separator row). A cell is a primitive (number → numeric cell, anything
// else → text) or `{ v, bold }` to render the value in bold.

const CRC_TABLE = (() => {
  const table = new Uint32Array(256);
  for (let n = 0; n < 256; n += 1) {
    let c = n;
    for (let k = 0; k < 8; k += 1) {
      c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
    }
    table[n] = c >>> 0;
  }
  return table;
})();

function crc32(bytes) {
  let crc = 0xffffffff;
  for (let i = 0; i < bytes.length; i += 1) {
    crc = CRC_TABLE[(crc ^ bytes[i]) & 0xff] ^ (crc >>> 8);
  }
  return (crc ^ 0xffffffff) >>> 0;
}

const encoder = new TextEncoder();

function escapeXml(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

// 0-based column index → spreadsheet column letter (0 → A, 26 → AA).
function columnLetter(index) {
  let letter = '';
  let n = index;
  do {
    letter = String.fromCharCode(65 + (n % 26)) + letter;
    n = Math.floor(n / 26) - 1;
  } while (n >= 0);
  return letter;
}

function cellXml(value, rowNumber, colIndex) {
  const ref = `${columnLetter(colIndex)}${rowNumber}`;
  const { v, bold } =
    typeof value === 'object' && value !== null ? value : { v: value };
  if (v == null || v === '') return '';
  const style = bold ? ' s="1"' : '';
  if (typeof v === 'number' && Number.isFinite(v)) {
    return `<c r="${ref}"${style}><v>${v}</v></c>`;
  }
  return `<c r="${ref}"${style} t="inlineStr"><is><t xml:space="preserve">${escapeXml(v)}</t></is></c>`;
}

function sheetXml(rows) {
  const body = rows
    .map((cells, rowIdx) => {
      const rowNumber = rowIdx + 1;
      if (!cells || !cells.length) return `<row r="${rowNumber}"/>`;
      const cellsXml = cells
        .map((cell, colIdx) => cellXml(cell, rowNumber, colIdx))
        .join('');
      return `<row r="${rowNumber}">${cellsXml}</row>`;
    })
    .join('');
  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main"><cols><col min="1" max="1" width="42" customWidth="1"/><col min="2" max="2" width="18" customWidth="1"/><col min="3" max="3" width="22" customWidth="1"/><col min="4" max="4" width="18" customWidth="1"/></cols><sheetData>${body}</sheetData></worksheet>`;
}

function sanitizeSheetName(name) {
  return String(name || 'Planilha')
    .replace(/[\\/?*[\]:]/g, ' ')
    .slice(0, 31);
}

function buildParts(rows, sheetName) {
  const styles = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<styleSheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main"><fonts count="2"><font><sz val="11"/><name val="Calibri"/></font><font><b/><sz val="11"/><name val="Calibri"/></font></fonts><fills count="2"><fill><patternFill patternType="none"/></fill><fill><patternFill patternType="gray125"/></fill></fills><borders count="1"><border/></borders><cellStyleXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0"/></cellStyleXfs><cellXfs count="2"><xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0"/><xf numFmtId="0" fontId="1" fillId="0" borderId="0" xfId="0" applyFont="1"/></cellXfs><cellStyles count="1"><cellStyle name="Normal" xfId="0" builtinId="0"/></cellStyles></styleSheet>`;

  return [
    {
      name: '[Content_Types].xml',
      data: `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types"><Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/><Default Extension="xml" ContentType="application/xml"/><Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/><Override PartName="/xl/worksheets/sheet1.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/><Override PartName="/xl/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml"/></Types>`,
    },
    {
      name: '_rels/.rels',
      data: `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships"><Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/></Relationships>`,
    },
    {
      name: 'xl/workbook.xml',
      data: `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships"><sheets><sheet name="${escapeXml(sanitizeSheetName(sheetName))}" sheetId="1" r:id="rId1"/></sheets></workbook>`,
    },
    {
      name: 'xl/_rels/workbook.xml.rels',
      data: `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships"><Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet1.xml"/><Relationship Id="rId2" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles" Target="styles.xml"/></Relationships>`,
    },
    { name: 'xl/styles.xml', data: styles },
    { name: 'xl/worksheets/sheet1.xml', data: sheetXml(rows) },
  ];
}

function u16(value) {
  return [value & 0xff, (value >>> 8) & 0xff];
}

function u32(value) {
  return [
    value & 0xff,
    (value >>> 8) & 0xff,
    (value >>> 16) & 0xff,
    (value >>> 24) & 0xff,
  ];
}

// Pack the parts into a ZIP archive using the "store" (no compression) method.
function zip(parts) {
  const fileRecords = [];
  let offset = 0;
  const localChunks = [];

  parts.forEach(part => {
    const nameBytes = encoder.encode(part.name);
    const dataBytes = encoder.encode(part.data);
    const crc = crc32(dataBytes);
    const header = [
      ...u32(0x04034b50), // local file header signature
      ...u16(20), // version needed
      ...u16(0), // flags
      ...u16(0), // compression: store
      ...u16(0), // mod time
      ...u16(0), // mod date
      ...u32(crc),
      ...u32(dataBytes.length), // compressed size
      ...u32(dataBytes.length), // uncompressed size
      ...u16(nameBytes.length),
      ...u16(0), // extra length
      ...nameBytes,
    ];
    localChunks.push(Uint8Array.from(header), dataBytes);
    fileRecords.push({ nameBytes, crc, size: dataBytes.length, offset });
    offset += header.length + dataBytes.length;
  });

  const centralChunks = [];
  let centralSize = 0;
  fileRecords.forEach(rec => {
    const central = [
      ...u32(0x02014b50), // central directory header signature
      ...u16(20), // version made by
      ...u16(20), // version needed
      ...u16(0), // flags
      ...u16(0), // compression
      ...u16(0), // mod time
      ...u16(0), // mod date
      ...u32(rec.crc),
      ...u32(rec.size), // compressed size
      ...u32(rec.size), // uncompressed size
      ...u16(rec.nameBytes.length),
      ...u16(0), // extra length
      ...u16(0), // comment length
      ...u16(0), // disk number start
      ...u16(0), // internal attrs
      ...u32(0), // external attrs
      ...u32(rec.offset),
      ...rec.nameBytes,
    ];
    centralChunks.push(Uint8Array.from(central));
    centralSize += central.length;
  });

  const end = Uint8Array.from([
    ...u32(0x06054b50), // end of central directory signature
    ...u16(0), // disk number
    ...u16(0), // disk with central directory
    ...u16(fileRecords.length),
    ...u16(fileRecords.length),
    ...u32(centralSize),
    ...u32(offset), // offset of central directory
    ...u16(0), // comment length
  ]);

  return new Blob([...localChunks, ...centralChunks, end], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
}

export const downloadXlsxFile = (fileName, rows, { sheetName } = {}) => {
  const blob = zip(buildParts(rows, sheetName));
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.setAttribute('download', fileName);
  link.setAttribute('href', url);
  link.click();
  URL.revokeObjectURL(url);
  return link;
};
